import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/services/api";
import Loader from "@/components/loader";
import { countryLabel } from "@/constants/countries";

const COLORS = ["purple", "warm", "cool", "rose"];

function initials(name = "") {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
}

function formatDate(d) {
  if (!d) return "";
  const date = new Date(d);
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function locationLine(c) {
  return [c.city, countryLabel(c.country)].filter(Boolean).join(" · ");
}

function jobRange(e) {
  if (!e?.from && !e?.to) return "";
  const y = (d) => (d ? new Date(d).getFullYear() : "now");
  return `${y(e.from)} — ${e.to ? y(e.to) : "now"}`;
}

function Card({ candidate, color, openByDefault }) {
  const [open, setOpen] = useState(openByDefault);
  const aiResult =
    candidate.screening_explanation || candidate.screening_score != null
      ? { result_explanation: candidate.screening_explanation, value: candidate.screening_score }
      : null;
  const strong = (aiResult?.value || 0) >= 70;
  return (
    <article className={`card ${open ? "open" : ""}`}>
      <div className="row" onClick={() => setOpen(!open)}>
        <div className={`avatar ${color}`}>{initials(candidate.name)}</div>
        <div className="who">
          <h3>{candidate.name || "Unnamed candidate"}</h3>
          {candidate.role ? <div className="role">{candidate.role}</div> : null}
        </div>
        <div className="meta-col">
          <span className="k">Location</span>
          <span className="v">{locationLine(candidate) || "—"}</span>
        </div>
        <div className="meta-col">
          <span className="k">Score</span>
          <span className="v">{candidate.screening_eliminated ? "Eliminated" : aiResult?.value != null ? `${aiResult.value} / 100` : "—"}</span>
        </div>
        <span className={`badge ${strong ? "match" : "warn"}`}>
          <span className="b-dot"></span>
          {strong ? "Strong match" : "Worth a look"}
        </span>
        <span className="chev">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </span>
      </div>
      <div className="body">
        <div className="body-inner">
          <div>
            {aiResult?.result_explanation ? (
              <div className={`verdict ${strong ? "" : "warn"}`}>
                <h4>
                  <span className="ring">
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </span>
                  AI assessment
                </h4>
                <p>{aiResult.result_explanation}</p>
              </div>
            ) : null}
          </div>
          <div className="resume-box">
            <h5>Recent experience</h5>
            {(candidate.experiences || []).length === 0 ? <div style={{ fontSize: 13, color: "var(--ink-3)" }}>No experience on file.</div> : null}
            {(candidate.experiences || []).map((e, i) => (
              <div key={i} className="job">
                <div className="years">{jobRange(e)}</div>
                <div>
                  <div className="title">{e.title || "—"}</div>
                  <div className="org">{e.company || ""}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </article>
  );
}

export default function PublicShortlist() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    load();
  }, [id]);

  async function load() {
    try {
      setLoading(true);
      const res = await api.get(`/stage/public/${id}`);
      if (!res.ok) {
        setError("This shortlist is not available.");
        return;
      }
      setData(res.data);
    } catch (e) {
      setError("This shortlist is not available.");
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#FAF8F5" }}>
        <Loader />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#FAF8F5", flexDirection: "column", gap: 12 }}>
        <h1 style={{ fontFamily: "'Instrument Serif', serif", fontSize: 48, margin: 0 }}>404</h1>
        <p style={{ color: "#7A7A82" }}>{error || "Not found"}</p>
      </div>
    );
  }

  const { stage, job, candidates } = data;
  const total = candidates.length;

  return (
    <>
      <style>{`
        :root {
          --bg: #FAF8F5; --paper: #FFFFFF; --ink: #1A1A1F; --ink-2: #45454D; --ink-3: #7A7A82;
          --line: #E8E4DC; --line-2: #F1EEE7;
          --accent: oklch(0.55 0.18 295); --accent-soft: oklch(0.96 0.03 295);
          --match: oklch(0.48 0.12 150); --match-soft: oklch(0.95 0.05 150);
          --warn: oklch(0.62 0.13 70); --warn-soft: oklch(0.97 0.04 80);
        }
        .ps-root, .ps-root * { box-sizing: border-box; }
        .ps-root { background: var(--bg); color: var(--ink); font-family: 'Geist', -apple-system, sans-serif; -webkit-font-smoothing: antialiased; min-height: 100vh; }
        .ps-root .topbar { display: flex; align-items: center; justify-content: space-between; padding: 16px 32px; border-bottom: 1px solid var(--line); position: sticky; top: 0; background: var(--bg); z-index: 5; }
        .ps-root .brand { display: flex; align-items: center; gap: 10px; font-family: 'Geist Mono', monospace; font-size: 12px; letter-spacing: 0.06em; color: var(--ink-2); }
        .ps-root .brand .dot { width: 8px; height: 8px; border-radius: 2px; background: var(--accent); }
        .ps-root .topbar .right { font-family: 'Geist Mono', monospace; font-size: 12px; color: var(--ink-3); }
        .ps-root .wrap { max-width: 1180px; margin: 0 auto; padding: 56px 40px 96px; }
        .ps-root .hero { display: grid; grid-template-columns: 1fr auto; align-items: end; gap: 32px; padding-bottom: 28px; border-bottom: 1px solid var(--line); margin-bottom: 8px; }
        .ps-root .eyebrow { font-family: 'Geist Mono', monospace; font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--ink-3); margin: 0 0 12px; }
        .ps-root h1 { font-family: 'Instrument Serif', serif; font-weight: 400; font-size: 52px; line-height: 1.0; letter-spacing: -0.01em; margin: 0 0 12px; }
        .ps-root h1 em { font-style: italic; color: var(--accent); }
        .ps-root .deck { font-size: 14.5px; color: var(--ink-2); margin: 0; max-width: 540px; line-height: 1.55; }
        .ps-root .summary { display: flex; flex-direction: column; align-items: flex-end; gap: 6px; }
        .ps-root .summary .big { font-family: 'Instrument Serif', serif; font-size: 56px; line-height: 1; }
        .ps-root .summary .lbl { font-family: 'Geist Mono', monospace; font-size: 11px; letter-spacing: 0.1em; color: var(--ink-3); text-transform: uppercase; }
        .ps-root .filters { display: flex; gap: 8px; padding: 18px 0 24px; flex-wrap: wrap; align-items: center; }
        .ps-root .filters .spacer { flex: 1; }
        .ps-root .filters .sort { font-family: 'Geist Mono', monospace; font-size: 11px; letter-spacing: 0.06em; color: var(--ink-3); text-transform: uppercase; }
        .ps-root .list { display: flex; flex-direction: column; gap: 14px; }
        .ps-root .card { background: var(--paper); border: 1px solid var(--line); border-radius: 8px; overflow: hidden; transition: border-color 0.18s ease, box-shadow 0.18s ease; }
        .ps-root .card.open { border-color: var(--ink); box-shadow: 0 24px 60px -40px rgba(20,20,30,0.3); }
        .ps-root .row { display: grid; grid-template-columns: 56px 1.5fr 1fr 1fr 140px 36px; gap: 24px; align-items: center; padding: 18px 22px; cursor: pointer; }
        .ps-root .row:hover { background: var(--bg); }
        .ps-root .card.open .row:hover { background: var(--paper); }
        .ps-root .avatar { width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-family: 'Instrument Serif', serif; font-size: 18px; flex-shrink: 0; border: 1px solid; }
        .ps-root .avatar.purple { background: var(--accent-soft); color: var(--accent); border-color: color-mix(in oklch, var(--accent) 25%, transparent); }
        .ps-root .avatar.warm { background: oklch(0.96 0.04 60); color: oklch(0.5 0.14 60); border-color: color-mix(in oklch, oklch(0.6 0.14 60) 25%, transparent); }
        .ps-root .avatar.cool { background: oklch(0.95 0.04 200); color: oklch(0.45 0.13 200); border-color: color-mix(in oklch, oklch(0.55 0.14 200) 25%, transparent); }
        .ps-root .avatar.rose { background: oklch(0.95 0.04 20); color: oklch(0.5 0.14 20); border-color: color-mix(in oklch, oklch(0.55 0.14 20) 25%, transparent); }
        .ps-root .who h3 { font-size: 16px; font-weight: 500; margin: 0 0 2px; }
        .ps-root .who .role { font-size: 13px; color: var(--ink-2); }
        .ps-root .meta-col { font-size: 13px; color: var(--ink-2); line-height: 1.5; }
        .ps-root .meta-col .k { display: block; font-family: 'Geist Mono', monospace; font-size: 10px; letter-spacing: 0.08em; color: var(--ink-3); text-transform: uppercase; margin-bottom: 2px; }
        .ps-root .meta-col .v { color: var(--ink); }
        .ps-root .badge { font-family: 'Geist Mono', monospace; font-size: 10.5px; letter-spacing: 0.06em; padding: 5px 10px 5px 8px; border-radius: 100px; display: inline-flex; align-items: center; gap: 6px; text-transform: uppercase; font-weight: 500; }
        .ps-root .badge.match { background: var(--match-soft); color: color-mix(in oklch, var(--match) 75%, black); }
        .ps-root .badge.warn { background: var(--warn-soft); color: color-mix(in oklch, var(--warn) 70%, black); }
        .ps-root .badge .b-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
        .ps-root .chev { width: 28px; height: 28px; border-radius: 50%; border: 1px solid var(--line); display: flex; align-items: center; justify-content: center; color: var(--ink-3); transition: transform 0.2s ease, color 0.2s ease, border-color 0.2s ease; justify-self: end; }
        .ps-root .card.open .chev { transform: rotate(180deg); border-color: var(--ink); color: var(--ink); }
        .ps-root .body { display: none; padding: 0 22px 24px; border-top: 1px solid var(--line-2); }
        .ps-root .card.open .body { display: block; }
        .ps-root .body-inner { display: grid; grid-template-columns: 1.1fr 1fr; gap: 40px; padding-top: 24px; }
        .ps-root .verdict { padding: 18px 20px; background: var(--match-soft); border: 1px solid color-mix(in oklch, var(--match) 25%, transparent); border-radius: 6px; margin-bottom: 20px; }
        .ps-root .verdict h4 { font-size: 12.5px; font-weight: 500; margin: 0 0 6px; color: color-mix(in oklch, var(--match) 78%, black); display: flex; align-items: center; gap: 8px; }
        .ps-root .verdict h4 .ring { width: 14px; height: 14px; border-radius: 50%; background: var(--match); color: var(--bg); display: flex; align-items: center; justify-content: center; }
        .ps-root .verdict p { margin: 0; font-size: 13px; line-height: 1.55; color: color-mix(in oklch, var(--match) 78%, black); }
        .ps-root .verdict.warn { background: var(--warn-soft); border-color: color-mix(in oklch, var(--warn) 25%, transparent); }
        .ps-root .verdict.warn h4 { color: color-mix(in oklch, var(--warn) 70%, black); }
        .ps-root .verdict.warn h4 .ring { background: var(--warn); }
        .ps-root .verdict.warn p { color: color-mix(in oklch, var(--warn) 70%, black); }
        .ps-root .resume-box h5 { font-family: 'Geist Mono', monospace; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--ink-3); margin: 0 0 14px; }
        .ps-root .job { padding: 12px 0; border-top: 1px solid var(--line-2); display: grid; grid-template-columns: 90px 1fr; gap: 16px; font-size: 13px; }
        .ps-root .job:first-of-type { border-top: 0; padding-top: 0; }
        .ps-root .job .years { font-family: 'Geist Mono', monospace; font-size: 11.5px; color: var(--ink-3); letter-spacing: 0.04em; padding-top: 2px; }
        .ps-root .job .title { font-weight: 500; margin: 0 0 2px; }
        .ps-root .job .org { color: var(--ink-2); font-size: 12.5px; }
        .ps-root footer { margin-top: 64px; padding-top: 24px; border-top: 1px solid var(--line); font-family: 'Geist Mono', monospace; font-size: 11px; color: var(--ink-3); letter-spacing: 0.04em; display: flex; justify-content: space-between; }
        .ps-root footer a { color: var(--accent); text-decoration: none; }
        @media (max-width: 980px) {
          .ps-root .row { grid-template-columns: 48px 1fr 100px 30px; row-gap: 6px; }
          .ps-root .row .meta-col { display: none; }
          .ps-root .body-inner { grid-template-columns: 1fr; }
          .ps-root .hero { grid-template-columns: 1fr; }
          .ps-root h1 { font-size: 36px; }
          .ps-root .summary { align-items: flex-start; flex-direction: row; align-items: baseline; gap: 12px; }
        }
      `}</style>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&family=Geist+Mono:wght@400;500&family=Instrument+Serif:ital@0;1&display=swap" rel="stylesheet" />
      <div className="ps-root">
        <div className="topbar">
          <div className="brand">
            <span className="dot"></span> jobego · shortlist
          </div>
          <div className="right">
            {stage.owner_name ? `Shared by ${stage.owner_name} · ` : ""}
            {stage.shared_at ? formatDate(stage.shared_at) : ""}
          </div>
        </div>

        <div className="wrap">
          <header className="hero">
            <div>
              <p className="eyebrow">
                {job?.organization_name ? `${job.organization_name} — ` : ""}
                {job?.title || "Role"}
                {job?.workplace_type ? ` (${String(job.workplace_type).toLowerCase()}${job?.country ? `, ${countryLabel(job.country)}` : ""})` : ""}
              </p>
              <h1>
                {stage.name}
                <br />
                <em>{total} {total === 1 ? "candidate" : "candidates"}.</em>
              </h1>
              {stage.description ? <p className="deck">{stage.description}</p> : null}
            </div>
            <div className="summary">
              <span className="big">{total}</span>
              <span className="lbl">candidates at this stage</span>
            </div>
          </header>

          <div className="filters">
            <div className="spacer"></div>
            <span className="sort">Ranked by fit</span>
          </div>

          <div className="list">
            {candidates.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "var(--ink-3)", fontSize: 14 }}>
                No candidates at this stage yet.
              </div>
            ) : (
              candidates
                .slice()
                .sort((a, b) => (b.screening_score ?? -1) - (a.screening_score ?? -1))
                .map((c, i) => <Card key={c.id} candidate={c} color={COLORS[i % COLORS.length]} openByDefault={i === 0} />)
            )}
          </div>

          <footer>
            <span>
              {stage.owner_name ? `Shared by ${stage.owner_name} · ` : ""}
              {stage.shared_at ? formatDate(stage.shared_at) : ""} · {total} {total === 1 ? "candidate" : "candidates"}
            </span>
            <span>
              Powered by <a href="https://jobego.co">Jobego</a>
            </span>
          </footer>
        </div>
      </div>
    </>
  );
}
